import { Routes } from '@common/constants';
import { INotificationType } from '@common/interfaces';
import { NEXT_MUTATION_ENTITY, NEXT_REST_ENTITY } from '@common/interfaces/entity';
import { TDelOrderPopupData } from '@components/common-components/custom-DeletePopup';
import { getCachedToken } from '@config/authentication';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectCartData, selectIsUsedCar, selectOrder, selectVehicleConfiguration } from '@store/selectors';
import { setCartData } from '@store/slices/cartDataSlice';
import {
  onCommonNotificationRaised,
  setIsCalled,
  setIsSalesWizardLoading,
  setIsShowDeleteModel,
  setIsVehicleRemoved,
  setListverkEquipments,
  setRimsEquipments,
  setSelectableEquipments,
  setSelectedVariantPath,
  setTaklakkEquipments,
} from '@store/slices/salesWizardSlice';
import { setSelectedVehicleStock } from '@store/slices/vehicleStockSlice';
import { getModelName } from '@utils/sales/vehicleOrder';
import { mutateNextEntityWithToken } from '@utils/serverActions/clientUtility';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useRestCall } from '../useRestCall';

export const useDeleteOrderData = () => {
  const isUsedCar = useAppSelector(selectIsUsedCar);
  const [removeNbfContract, setRemoveNbfContract] = useState<boolean>(false);
  const order = useAppSelector(selectOrder);
  const searchParams = useSearchParams();
  const make = searchParams.get('merke');
  const cartData = useAppSelector(selectCartData);
  const vehicle = useAppSelector(selectVehicleConfiguration);
  useRestCall(NEXT_REST_ENTITY.DELETE_NBF_CONTRACTS, {
    method: 'DELETE',
    body: { orderId: order?.id },
    nextOptions: { revalidate: 60 },
    defaultLoading: true,
    skip: !(order?.nbfContractStoredLocally && removeNbfContract),
    onCompleted: async () => {
      await removeVehicleFromOrder(order, make);
    },
  });

  const removeVehicleFromOrder = async (order, make) => {
    dispatch(setIsSalesWizardLoading(true));

    let data = null;
    try {
      let variables = {
        orderId: order?.id,
        options: null,
        vehicles: null,
        vatCalculation: null,
      };

      data = await mutateNextEntityWithToken(
        NEXT_MUTATION_ENTITY.REMOVE_VEHICLES_FROM_ORDER,
        getCachedToken(),
        variables,
        undefined,
        order?.id
      );
      dispatch(setIsVehicleRemoved(true));
      dispatch(
        setCartData({
          ...cartData,
          make: getModelName(vehicle, make, order),
          modelSeries: '',
          data: [],
          image: null,
        })
      );
      dispatch(setRimsEquipments(null));
      dispatch(setSelectableEquipments([]));
      dispatch(setListverkEquipments(null));
      dispatch(setTaklakkEquipments(null));
      dispatch(setSelectedVehicleStock(null));
      dispatch(setSelectedVariantPath(null));
      dispatch(setIsSalesWizardLoading(false));
      const route = Routes.getVehiclePickerPage(order?.id);
      dispatch(setIsCalled(false));
      if (typeof window !== 'undefined') {
        window.location.href = route;
      }
    } catch (error) {
      console.error('Failed to remove vehicle from order', error);
      dispatch(
        onCommonNotificationRaised({
          key: 'removeVehicleFail',
          type: INotificationType.REMOVE_VEHICLE_FAIL,
          orderId: order?.id,
          errorCode: data?.removeVehiclesFromOrder?.errorCode,
        })
      );
    }
  };

  const dispatch = useAppDispatch();

  const deleteOrderData: TDelOrderPopupData = {
    title: `Er du sikker på at du vil fjerne valgt bil?`,
    description: isUsedCar
      ? `Dersom du fjerner valgt bil sendes du tilbake til steg 1 en ny bil eller velge lagerbil på nytt.`
      : `Dersom du fjerner valgt bil sendes du tilbake til steg 1 for å konfigurere en ny bil eller velge lagerbil på nytt.`,
    actionsList: [
      {
        actionLabel: 'Ja, fjern valgt bil',
        actionMethod: () => {
          if (order?.nbfContractStoredLocally) {
            setRemoveNbfContract(true);
          } else {
            removeVehicleFromOrder(order, make);
          }
          dispatch(setIsShowDeleteModel(false));
        },
        actionClassNames: 'tw-solid-red',
      },
      {
        actionLabel: 'Avbryt',
        actionMethod: () => dispatch(setIsShowDeleteModel(false)),
      },
    ],
  };

  return deleteOrderData;
};
